import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  failedEmailEventTypes,
  IEmailReport,
  IEmailReportRow,
  SavedFilterType,
  SendgridMessageEventType,
} from "shared";
import { toast } from "react-toastify";
import {
  ExportCsv,
  FilterPanelTrigger,
  getGridSingleSelectOperators,
  getGridStringOperators,
  gridFilteredSortedRowIdsSelector,
  GridColDef,
  GridCsvExportOptions,
  GridFilterListIcon,
  GridFilterModel,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid";
import {
  DataGridPro,
  gridFilterModelSelector,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { Download } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { FunnelIcon } from "@heroicons/react/24/outline";

import { useApiRequest } from "../../../hooks/useApiRequest";
import { MultiOrgSelector } from "../MultiOrgSelector";
import { commonConfig, getTogglableColumns } from "../../../lib/datagrid";
import { ApiSavedFilterPanel } from "../../Common/Datagrid/ApiSavedFilterPanel";
import { SaveFilterButton } from "../../Common/Datagrid/SaveFilterButton";
import { DatagridCard } from "../../Common/Datagrid/DatagridCard";
import { useApplySavedFilterFromUrl } from "../../../hooks/useApplySavedFilterFromUrl";
import { useOrganisationsIncludeDisconnected } from "../../../hooks/useOrganisationsIncludeDisconnected";
import { EmailStatusPill, emailStatusLabel } from "../../Common/EmailStatusPill";
import { Button } from "../../../../common/Atoms/Button";
import {
  buildOrganisationsFilterColumn,
  getReportFilterValue,
  ReportFilterField,
  reportFilterColumnVisibility,
  reportFilterFieldList,
  withReportFilterValue,
} from "../reportGridFilters";

const baseColumns: GridColDef<IEmailReportRow>[] = [
  {
    field: `organisationName`,
    headerName: `Entity Name`,
    minWidth: 130,
    // Use the Entities report filter instead
    filterable: false,
  },
  {
    field: `contactName`,
    headerName: `Contact Name`,
    minWidth: 130,
    // Applied server-side (rows are paginated)
    filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
  },
  {
    field: `toAddress`,
    headerName: `To`,
    minWidth: 180,
    filterable: false,
  },
  {
    field: `subject`,
    headerName: `Subject`,
    minWidth: 220,
    flex: 1,
    filterable: false,
  },
  {
    field: `sentAtIso`,
    headerName: `Sent`,
    type: `dateTime`,
    minWidth: 160,
    filterable: false,
    valueGetter: value => value ? new Date(value) : null,
  },
  {
    field: `latestStatus`,
    headerName: `Latest Status`,
    type: `singleSelect`,
    minWidth: 130,
    valueOptions: Object.values(SendgridMessageEventType).map(status => ({
      value: status,
      label: emailStatusLabel(status),
    })),
    // Applied server-side (rows are paginated), so only operators the API
    // understands are offered
    filterOperators: getGridSingleSelectOperators().filter(op => [`is`, `isAnyOf`].includes(op.value)),
    renderCell: ({ row }) => (
      <div className={ `flex h-full items-center` }>
        <EmailStatusPill status={ row.latestStatus } />
      </div>
    ),
  },
];

export function EmailReport() {
  const apiRequest = useApiRequest();
  const apiRef = useGridApiRef();
  const [reportResponse, setReportResponse] = useState<IEmailReport>({
    rows: [],
    total: 0,
  });
  const [reportLoading, setReportLoading] = useState(false);
  // The org selection lives in the grid filter model so it is captured by
  // saved filter presets
  const [searchParams] = useSearchParams();
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    // Allow deep links (e.g. the dashboard email widget) to preload the org
    const orgs = searchParams.getAll(`org`);

    return withReportFilterValue({ items: [] }, ReportFilterField.organisations, `isAnyOf`, orgs);
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: `sentAtIso`, sort: `desc` }]);

  const { data: allOrganisations } = useOrganisationsIncludeDisconnected();

  const orgOptions = useMemo(() => {
    return allOrganisations.map(org => ({
      value: org.id,
      label: org.legalName,
    }));
  }, [allOrganisations]);

  const selectedOrgs = useMemo(() => {
    const value = getReportFilterValue(filterModel, ReportFilterField.organisations);

    return Array.isArray(value) ? value as string[] : [];
  }, [filterModel]);

  const selectedStatuses = useMemo(() => {
    const item = filterModel.items.find(i => i.field === `latestStatus`);

    if (!item || item.value === null || item.value === undefined || item.value === ``) {
      return [];
    }

    return Array.isArray(item.value) ? item.value as string[] : [item.value as string];
  }, [filterModel]);

  const contactFilter = useMemo(() => {
    const value = filterModel.items.find(i => i.field === `contactName`)?.value;

    return typeof value === `string` && value.trim() !== `` ? value.trim() : undefined;
  }, [filterModel]);

  // Serialised so the fetch effect only re-runs when the server-side inputs change
  const reportQueryKey = JSON.stringify({
    orgs: selectedOrgs,
    statuses: selectedStatuses,
    contact: contactFilter,
    sortField: sortModel[0]?.field,
    sortDir: sortModel[0]?.sort || undefined,
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
  });

  useEffect(() => {
    const params = JSON.parse(reportQueryKey) as {
      orgs: string[];
      statuses: string[];
      contact?: string;
      sortField?: string;
      sortDir?: string;
      page: number;
      pageSize: number;
    };

    async function fetchReport() {
      try {
        setReportLoading(true);

        const response = await apiRequest
          .get<IEmailReport>(`/reports/email`, {
            params,
          });

        setReportResponse(response.data);
        setReportLoading(false);
      }
      catch (err) {
        setReportLoading(false);
        console.error(`Error fetching email report`, err);
        toast.error(`Could not fetch email report. ${err?.response?.data?.toString() || ``}`);
      }
    }

    if (params.orgs.length > 0) {
      fetchReport();
    }

    if (params.orgs.length === 0) {
      setReportResponse({
        rows: [],
        total: 0,
      });

      setReportLoading(false);
    }
  }, [reportQueryKey, apiRequest]);

  useApplySavedFilterFromUrl(apiRef, SavedFilterType.ReportEmail, {
    defaultColumnVisibilityModel: reportFilterColumnVisibility,
  });

  function resetPage() {
    setPaginationModel(model => model.page === 0 ? model : { ...model, page: 0 });
  }

  function setSelectedOrgs(orgs: string[]) {
    setFilterModel(model => withReportFilterValue(model, ReportFilterField.organisations, `isAnyOf`, orgs));
    resetPage();
  }

  function handleFilterModelChange(model: GridFilterModel) {
    setFilterModel(model);
    resetPage();
  }

  function handleSortModelChange(model: GridSortModel) {
    setSortModel(model);
    resetPage();
  }

  const columns: GridColDef<IEmailReportRow>[] = useMemo(() => {
    return [
      ...baseColumns,
      // Hidden column carrying the org selection in the filter model
      buildOrganisationsFilterColumn(orgOptions),
    ];
  }, [orgOptions]);

  // Sub rows: one per status/event update on the email
  const getDetailPanelContent = useCallback((params: GridRowParams<IEmailReportRow>) => {
    if (!params.row.events?.length) {
      return null;
    }

    return (
      <div className={ `px-12 py-2 bg-gray-50` }>
        <table className={ `text-sm w-full` }>
          <tbody>
            { params.row.events.map(event => (
              <tr key={ event.id }>
                <td className={ `py-1 w-40` }>
                  <EmailStatusPill status={ event.event } />
                </td>
                <td className={ `py-1 w-64 text-gray-600` }>
                  { event.email || `` }
                </td>
                <td className={ `py-1 w-56 text-gray-600` }>
                  { event.timestampIso ? new Date(event.timestampIso).toLocaleString() : `-` }
                </td>
                <td className={ `py-1 text-gray-600` }>
                  { event.reason || `` }
                </td>
              </tr>
            )) }
          </tbody>
        </table>
      </div>
    );
  }, []);

  const viewportHeight = window.innerHeight;
  const tableHeight = viewportHeight
    - 180; // px for the header

  return (
    <div className={ `flex flex-col h-full` }
      style={ { height: tableHeight } }>
      <div className={ `flex gap-4 mb-4 items-end` }>
        <div className={ `w-1/3` }>
          <MultiOrgSelector
            onChange={ setSelectedOrgs }
            selected={ selectedOrgs }
          />
        </div>
        <div className={ `ml-auto text-xs text-gray-500` }>
          { `Open and click tracking is best effort — some email clients block tracking, so these events may not always be recorded.` }
        </div>
      </div>
      <DatagridCard className={ `flex-1` }>
        <DataGridPro
          apiRef={ apiRef }
          columns={ columns }
          rows={ reportResponse.rows }
          loading={ reportLoading }
          getRowId={ row => row.id }
          rowHeight={ 38 }
          filterModel={ filterModel }
          onFilterModelChange={ handleFilterModelChange }
          filterMode={ `server` }
          sortingMode={ `server` }
          sortModel={ sortModel }
          onSortModelChange={ handleSortModelChange }
          pagination
          paginationMode={ `server` }
          rowCount={ reportResponse.total }
          paginationModel={ paginationModel }
          onPaginationModelChange={ setPaginationModel }
          pageSizeOptions={ [25, 50, 100] }
          getDetailPanelContent={ getDetailPanelContent }
          getDetailPanelHeight={ () => `auto` }
          initialState={ {
            columns: {
              columnVisibilityModel: reportFilterColumnVisibility,
            },
          } }
          slots={ { toolbar: CustomToolbar, filterPanel: ApiSavedFilterPanel } }
          showToolbar
          slotProps={ {
            columnsManagement: {
              getTogglableColumns: columns => getTogglableColumns(columns, reportFilterFieldList),
            },
            filterPanel: {
              savedFilterType: SavedFilterType.ReportEmail,
            },
          } }
          sx={ {
            ...commonConfig.headerStyle,
          } }
        />
      </DatagridCard>
    </div>
  );
}

function CustomToolbar() {
  const apiRef = useGridApiContext();

  function applyFailedQuickFilter() {
    const model = gridFilterModelSelector(apiRef);

    apiRef.current.setFilterModel(withReportFilterValue(model, `latestStatus`, `isAnyOf`, failedEmailEventTypes));
  }

  const csvOptions: GridCsvExportOptions = {
    fileName: `email_report`,
    // Default export only includes selected rows when one is highlighted;
    // always export the whole page instead
    getRowsToExport: ({ apiRef }) => gridFilteredSortedRowIdsSelector(apiRef),
  };

  return (
    <Toolbar>
      <Tooltip title={ `Filters` }>
        <FilterPanelTrigger render={ <ToolbarButton /> }>
          <GridFilterListIcon fontSize={ `medium` } />
        </FilterPanelTrigger>
      </Tooltip>
      <SaveFilterButton type={ SavedFilterType.ReportEmail } />
      <Button
        onClick={ applyFailedQuickFilter }
        size={ `sm` }
        color={ `paidniceGreenOutline` }
      >
        <FunnelIcon className={ `w-4 h-4 mr-1` } />
        { `Failed emails` }
      </Button>
      <div className={ `flex justify-end w-full` }>
        <ExportCsv
          render={ <ToolbarButton /> }
          options={ csvOptions }
        >
          <Download fontSize={ `medium` }/>
        </ExportCsv>
      </div>
    </Toolbar>
  );
}
